import { css } from '@emotion/react'
import { Alert, Button, Heading, Icon, VFlow } from 'bold-ui'
import { FieldArray, FieldArrayRenderProps } from 'components/form'
import { resolveName } from 'components/form/core/hooks/useField'
import { PrivateRoute } from 'components/route'
import { useArquivosAnexadosNoProntuarioQuery } from 'graphql/hooks.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useConfiguracoes } from 'hooks/configuracoes'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { emptyArrayOf } from 'util/array'
import { MetaArray } from 'util/metaPath'

import { grupoCboAnexoArquivos } from '../acessos'
import { AnexoArquivosModal } from './components/AnexoArquivosModal'
import { AnexoArquivosTable } from './components/table/AnexoArquivosTable'
import { AnexoArquivoFormModel } from './model-anexoArquivos'

interface AnexoArquivosViewProps {
  name: MetaArray<AnexoArquivoFormModel>
}

const ANEXO_ARQUIVOS_MODAL_URL = '/anexo-arquivo'

export function AnexoArquivosView(props: AnexoArquivosViewProps) {
  const { name } = props

  const history = useHistory()
  const match = useRouteMatch()

  const openModal = () => {
    history.push(`${match.path}${ANEXO_ARQUIVOS_MODAL_URL}`)
  }

  const closeModal = () => {
    history.push(match.url.replace(`${ANEXO_ARQUIVOS_MODAL_URL}`, ''))
  }

  const {
    configuracoes: { anexoArquivosEnabled },
  } = useConfiguracoes()

  const {
    atendimentoProfissional: {
      id: atendimentoProfissionalId,
      lotacao: { id: lotacaoId, profissional, cbo },
    },
    prontuario: { id: prontuarioId },
    isRetificacao,
  } = useAtendimentoContext()

  const { data, loading } = useArquivosAnexadosNoProntuarioQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      prontuarioId,
    },
    skip: isRetificacao,
  })

  const renderAnexos = anexoArquivosEnabled || !(data.arquivosAnexadosNoProntuario ?? emptyArrayOf()).isEmpty()

  const renderFieldArray = (fieldArrayRenderProps: FieldArrayRenderProps<AnexoArquivoFormModel, any>) => {
    const {
      fields: { push, value, remove, update },
      meta: { invalid },
    } = fieldArrayRenderProps

    return (
      <VFlow>
        {renderAnexos && <Heading level={4}>Anexo de arquivos</Heading>}
        {anexoArquivosEnabled && (
          <>
            <Button kind='primary' size='small' data-cy='AnexoArquivos.AnexarArquivo' onClick={openModal}>
              <Icon
                icon='upload'
                style={css`
                  margin-right: 0.5rem;
                `}
              />
              Anexar arquivo
            </Button>
            <PrivateRoute
              cbos={grupoCboAnexoArquivos}
              path={`${match.path}${ANEXO_ARQUIVOS_MODAL_URL}`}
              render={() => <AnexoArquivosModal onClose={closeModal} onConfirm={push} />}
            />
          </>
        )}
        {invalid && (
          <Alert inline type='danger'>
            Existem campos preenchidos de forma incorreta.
          </Alert>
        )}
        {renderAnexos && (
          <AnexoArquivosTable
            atendimentoProfissionalId={atendimentoProfissionalId}
            lotacaoId={lotacaoId}
            profissional={profissional}
            cbo={cbo}
            arquivosAnexados={value}
            anexoArquivosLoading={loading}
            arquivosAnexadosDatabase={data.arquivosAnexadosNoProntuario}
            onRemoveRow={remove}
            onUpdateRow={update}
          />
        )}
      </VFlow>
    )
  }

  return <FieldArray<AnexoArquivoFormModel> name={resolveName(name)} render={renderFieldArray} />
}
